// SCR-CAR, MOD-CAR-NODE, SCR-TRIPS, MOD-TRIP-START, MOD-TRIP-RESULT (E06-T07) и «Подлатать» (E06-T14) — ux/06.
// Улучшение и подлатывание — разные эндпоинты (…/upgrade и …/patch), в теле — цена, которую видел игрок
// (expectedCostNuts); PRICE_CHANGED — кнопка показывает новую цену и ждёт повторного тапа.
import { signal } from '@preact/signals';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Api, ResponseOf } from '@garages/contract';
import { useApp } from '../app/app.js';
import { CAR_NODES, carStage, ROUTES, ROUTE_LOCK } from '../content/garage.js';
import { Icon } from '../assets/ui.js';
import { amount, duration, mskTime, num, timer } from '../i18n/format.js';
import { t as tr } from '../i18n/t.js';
import { ApiCallError } from '../net/api-client.js';
import { online, player, setWallet, tutorialStep, wallet } from '../state/game.js';
import type { ScreenProps, SheetProps } from '../ui/App.js';
import { ErrorBanner, ScreenTitle, Sheet, Skeleton, useActionBar, useNow } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { useLoad } from '../ui/use-load.js';
import { I } from '../assets/icons.js';
import { setTut, TEXT, tutT08Repair } from '../tutorial/flow.js';
import { assetUrl, manifest } from '../assets/manifest.js';

type Car = ResponseOf<Api['getCar']>;
type CarNode = Car['nodes'][number];
type Trips = ResponseOf<Api['getTrips']>;
type Trip = NonNullable<Trips['running']>;
export const car = signal<Car | null>(null);
export const trips = signal<Trips | null>(null);
const NODE_ORDER = ['engine', 'body', 'chassis'] as const;

export function conditionWord(p: number): string {
  return p < 30 ? tr('CL-CAR-BAD') : p < 70 ? tr('CL-CAR-SO-SO') : tr('CL-CAR-OK');
}

/** Средняя справность по узлам, которые уже есть (уровень ≥ 1). */
export function carCondition(nodes: CarNode[]): number {
  const live = nodes.filter((n) => n.level > 0);
  return live.length ? Math.round(live.reduce((s, n) => s + n.condition, 0) / live.length) : 0;
}

export function tripState(t: Trip | null, now: number): 'home' | 'running' | 'back' {
  if (!t || t.status === 'claimed') return 'home';
  return t.status === 'done' || Date.parse(t.finishAt) <= now ? 'back' : 'running';
}

async function refresh(app: ReturnType<typeof useApp>) {
  const [c, t] = await Promise.all([app.call('getCar'), app.call('getTrips')]);
  car.value = c;
  trips.value = t;
}

/** Цена, которую видел игрок; PRICE_CHANGED — новая цена из details и повторный тап (ux/06 §2). */
async function priced<T>(app: ReturnType<typeof useApp>, run: (expected: number) => Promise<T>, expected: number, onNewPrice: (n: number) => void): Promise<T | null> {
  try { return await run(expected); } catch (e) {
    if (e instanceof ApiCallError && e.code === 'PRICE_CHANGED') {
      const n = Number(e.details?.['costNuts'] ?? 0);
      onNewPrice(n);
      showToast(tr('CL-CAR-PRICE-CHANGED', { n: num(n) }));
      return null;
    }
    app.handleError(e, { resource: tr('CL-GARAGE-NUTS') });
    void refresh(app).catch(() => {});
    return null;
  }
}

function NodeCard(p: { n: CarNode; inTrip: boolean }) {
  const app = useApp();
  const info = CAR_NODES[p.n.nodeId];
  const bar = p.n.condition < 30 ? 'var(--negative)' : p.n.condition < 70 ? 'var(--warning)' : 'var(--positive)';
  return (
    <button class="list-row" data-testid={`node-${p.n.nodeId}`} data-tut={`node-${p.n.nodeId}`} style={{ minHeight: 72, opacity: p.inTrip ? 0.6 : 1 }} onClick={() => { app.haptic('selection'); app.nav.openSheet('MOD-CAR-NODE', { nodeId: p.n.nodeId }); }}>
      <Icon id={info.art} fallback={info.icon} size={40} />
      <span class="col" style={{ gap: 4, flex: 1 }}>
        <span class="row" style={{ justifyContent: 'space-between' }}><b>{info.name}</b><span class="small muted">{tr('CL-CAR-LVL', { n: p.n.level, max: 5 })}</span></span>
        {p.n.level ? (
          <span class="row" style={{ gap: 8 }}>
            <span class="progress" style={{ flex: 1 }}><i style={{ width: `${p.n.condition}%`, background: bar }} /></span>
            <span class="small">{p.n.condition}% · {conditionWord(p.n.condition)}</span>
          </span>
        ) : <span class="small muted">{info.stages[0]}</span>}
        <span class="small muted">{p.inTrip ? tr('CL-CAR-IN-TRIP-ROW') : info.effect}</span>
      </span>
    </button>
  );
}

/** SCR-CAR (ux/06 §1). */
export function CarScreen(_p: ScreenProps) {
  const app = useApp();
  const load = useLoad(async () => { await refresh(app); return car.value!; }, { cached: car.value });
  const now = useNow();
  const c = car.value;
  void manifest.value;
  const carUrl = assetUrl(`CAR-L${carStage(c?.nodes ?? [])}`);
  const run = trips.value?.running ?? null;
  const st = tripState(run, now);
  const [patchAll, setPatchAll] = useState<number | null>(null);
  const key = useRef(app.idem());
  const cost = patchAll ?? c?.patchAllCost ?? 0;
  const lack = Math.max(0, cost - (wallet.value?.nuts ?? 0));
  const anyBad = !!c?.nodes.some((n) => n.level > 0 && n.condition < 100);
  // T08 (ux/11 §1.3): «Осмотр» снимает брезент — до него узлов не видно; затем цель — двигатель.
  const tutT08 = tutorialStep.value === 'T08';
  const [inspected, setInspected] = useState(!tutT08);
  useEffect(() => { if (tutT08) setTut({ target: inspected ? 'node-engine' : 'car-inspect', say: null, hint: inspected ? TEXT.T08nodes : null }); }, [tutT08, inspected]);
  const doPatchAll = async () => {
    const r = await priced(app, (exp) => app.call('patchAll', { body: { expectedCostNuts: exp }, idempotencyKey: key.current }), cost, setPatchAll);
    if (!r) return;
    key.current = app.idem();
    setPatchAll(null);
    car.value = { ...c!, nodes: r.nodes, patchAllCost: 0 };
    setWallet(r.wallet);
    app.haptic('success');
  };
  useActionBar({
    back: false,
    ...(st === 'back'
      ? { main: { text: tr('CL-CAR-CLAIM'), testId: 'trip-claim', disabled: !online.value, onClick: () => app.nav.openSheet('MOD-TRIP-RESULT', { tripId: run!.tripId }) } }
      : st === 'running'
        ? { main: { text: tr('CL-CAR-RUNNING', { t: timer(Date.parse(run!.finishAt) - now) }), disabled: true, onClick: () => {} } }
        : anyBad && cost > 0
          ? { main: lack
            ? { text: tr('CL-GARAGE-LACK', { n: num(lack) }), tone: 'outline' as const, testId: 'patch-all', onClick: () => app.nav.openSheet('MOD-RESOURCE-INFO', { currency: 'nuts' }) }
            : { text: tr('T-NODE-PATCH-ALL-COST', { cost: num(cost) }), icon: 'nuts' as const, testId: 'patch-all', disabled: !online.value, onClick: doPatchAll } }
          : {}),
    ...(st !== 'running' ? { secondary: { text: tr('CL-CAR-TRIPS'), tone: 'outline' as const, testId: 'open-trips', onClick: () => app.nav.push('SCR-TRIPS') } } : {}),
  });
  return (
    <>
      <ScreenTitle right={c ? <span class="small">{tr('T-CAR-CONDITION', { p: carCondition(c.nodes) })}</span> : null}>{tr('UI-CAR-TITLE')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-CAR">
        <div class="page col">
          {load.error && !load.offline ? <ErrorBanner what={tr('CL-CAR-WHAT')} onRetry={load.reload} /> : null}
          <div class="car-stage" aria-hidden>
            {st === 'running' ? <span class="muted">{tr('CL-CAR-GONE', { route: ROUTES[run!.tripDefId]?.name ?? run!.tripDefId, time: mskTime(Date.parse(run!.finishAt)) })}</span>
              : (carUrl ? <img src={carUrl} alt="" /> : null)}
            {st === 'back' ? <span class="badge" style={{ position: 'absolute', top: 8, right: 8 }}>{tr('UI-HOME-CAR-BACK')}</span> : null}
          </div>
          {tutT08 && !inspected ? <button class="btn accent" data-testid="car-inspect" data-tut="car-inspect" onClick={() => { app.haptic('medium'); setInspected(true); }}>{TEXT.T08inspect}</button> : null}
          {!c || !inspected ? (c ? null : <Skeleton h={72} n={3} />) : NODE_ORDER.map((id) => { const n = c.nodes.find((x) => x.nodeId === id); return n ? <NodeCard key={id} n={n} inTrip={st === 'running'} /> : null; })}
          {c && !c.tripsAvailable ? <div class="banner">{tr('CL-CAR-TRIPS-LOCKED')}</div> : null}
          {c?.nodes.some((n) => n.level > 0 && n.condition < 30) ? <div class="banner warn">{tr('CL-CAR-PATCH-FIRST')}</div> : null}
        </div>
      </div>
    </>
  );
}

/** MOD-CAR-NODE (ux/06 §2): «Улучшить за {cost}» и «Подлатать за {cost}» — разные кнопки и эндпоинты. */
export function CarNodeSheet({ entry }: SheetProps) {
  const app = useApp();
  const id = entry.params['nodeId'] as CarNode['nodeId'];
  const n = car.value?.nodes.find((x) => x.nodeId === id);
  const info = CAR_NODES[id];
  const me = player.value;
  const w = wallet.value;
  useEffect(() => { if (tutorialStep.value === 'T08' && id === 'engine') setTut({ target: 'node-repair', say: null, hint: null }); }, []);
  const [upCost, setUpCost] = useState<number | null>(null);
  const [patchCost, setPatchCost] = useState<number | null>(null);
  const key = useRef(app.idem());
  const inTrip = tripState(trips.value?.running ?? null, Date.now()) === 'running';
  if (!n) return <Sheet entry={entry} title={info.name}><Skeleton h={100} /></Sheet>;
  const up = upCost ?? n.upgradeCost?.nuts ?? 0;
  const patch = patchCost ?? n.patchCost;
  const apply = (node: CarNode, wl: ResponseOf<Api['getWallet']>) => {
    key.current = app.idem();
    const nodes = car.value!.nodes.map((x) => (x.nodeId === node.nodeId ? node : x));
    car.value = { ...car.value!, nodes, patchAllCost: nodes.reduce((s, x) => s + x.patchCost, 0) };
    setWallet(wl);
    app.haptic('success');
  };
  const doUp = async () => {
    const r = await priced(app, (exp) => app.call('upgradeNode', { params: { nodeId: id }, body: { expectedCostNuts: exp }, idempotencyKey: key.current }), up, setUpCost);
    if (r) { setUpCost(null); apply(r.node, r.wallet); }
  };
  const doPatch = async () => {
    const r = await priced(app, (exp) => app.call('patchNode', { params: { nodeId: id }, body: { expectedCostNuts: exp }, idempotencyKey: key.current }), patch, setPatchCost);
    if (r) { setPatchCost(null); apply(r.node, r.wallet); }
  };
  const off = !online.value || inTrip;
  const lowLevel = !!n.upgradeCost && !!me && me.level < n.upgradeCost.playerLevel;
  const lackUp = n.upgradeCost ? Math.max(0, up - (w?.nuts ?? 0)) : 0;
  const lackPatch = Math.max(0, patch - (w?.nuts ?? 0));
  const actions = [];
  if (n.upgradeCost) {
    actions.push(lowLevel
      ? { text: tr('CL-GARAGE-NEED-LEVEL', { n: n.upgradeCost.playerLevel }), disabled: true, testId: 'node-upgrade', onClick: () => {} }
      : lackUp ? { text: tr('CL-GARAGE-LACK', { n: num(lackUp) }), tone: 'outline' as const, testId: 'node-upgrade', onClick: () => app.nav.openSheet('MOD-RESOURCE-INFO', { currency: 'nuts' }) }
        : { text: tr('CL-CAR-UPGRADE', { cost: num(up) }), icon: 'nuts' as const, testId: 'node-upgrade', disabled: off, onClick: doUp });
  }
  // T08: «Починить» двигатель — бесплатно, ур. 1 выдаёт сервер по ключу шага (E16-T01).
  if (tutorialStep.value === 'T08' && id === 'engine' && n.level === 0) {
    actions.splice(0, actions.length, { text: TEXT.T08btn, testId: 'node-repair', disabled: off, onClick: () => tutT08Repair(app, tr('TUT-T08-06', { lvl: 4 })) });
  }
  if (patch > 0 && !(tutorialStep.value === 'T08' && id === 'engine')) {
    actions.unshift(lackPatch
      ? { text: tr('CL-GARAGE-LACK', { n: num(lackPatch) }), tone: 'outline' as const, testId: 'node-patch', onClick: () => app.nav.openSheet('MOD-RESOURCE-INFO', { currency: 'nuts' }) }
      : { text: tr('T-NODE-PATCH-COST', { cost: num(patch) }), tone: 'outline' as const, icon: 'nuts' as const, testId: 'node-patch', disabled: off, onClick: doPatch });
  }
  return (
    <Sheet entry={entry} title={info.name} actions={actions}>
      <div class="col" data-testid="MOD-CAR-NODE-body">
        <span class="muted">{info.desc}</span>
        <b>{tr('CL-CAR-NODE-LVL', { from: n.level, to: Math.min(5, n.level + 1), stage: info.stages[n.level] ?? '' })}</b>
        <span class="small">{info.effect}</span>
        {n.level ? (
          <div class="col" style={{ gap: 4 }}>
            <span class="row" style={{ justifyContent: 'space-between' }}><span>{tr('T-CAR-CONDITION', { p: n.condition })}</span><span class="small muted">{conditionWord(n.condition)}</span></span>
            <span class="progress"><i style={{ width: `${n.condition}%` }} /></span>
          </div>
        ) : null}
        {n.upgradeCost ? <span class="small">{tr('CL-CAR-UP-COST', { nuts: num(up), scrap: n.upgradeCost.scrap })}</span> : null}
        {!n.upgradeCost && patch === 0 ? <div class="banner pos">{tr('T-NODE-MAX')}</div> : null}
        {inTrip ? <div class="banner">{tr('CL-CAR-IN-TRIP-ROW')}</div> : null}
      </div>
    </Sheet>
  );
}

/** SCR-TRIPS (ux/06 §3): статус, маршруты, закрытые — с причиной; «Вернуть сейчас» — по данным сервера. */
export function TripsScreen(_p: ScreenProps) {
  const app = useApp();
  const load = useLoad(async () => { await refresh(app); return trips.value!; }, { cached: trips.value });
  const now = useNow();
  const t = trips.value;
  const run = t?.running ?? null;
  const st = tripState(run, now);
  useActionBar({
    back: true,
    ...(st === 'back' ? { main: { text: tr('CL-CAR-CLAIM'), testId: 'trip-claim', disabled: !online.value, onClick: () => app.nav.openSheet('MOD-TRIP-RESULT', { tripId: run!.tripId }) } } : {}),
    ...(t && !t.available.some((r) => r.available) && st === 'home' ? { main: { text: tr('CL-CAR-TO-CAR'), onClick: () => app.nav.pop() } } : {}),
  });
  return (
    <>
      <ScreenTitle>{tr('CL-CAR-TRIPS')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-TRIPS">
        <div class="page col">
          {load.error && !load.offline ? <ErrorBanner what={tr('CL-CAR-ROUTES-WHAT')} onRetry={load.reload} /> : null}
          <div class="card" data-testid="trip-status" style={{ minHeight: 72 }}>
            {st === 'home' ? tr('CL-CAR-HOME') : st === 'running'
              ? tr('CL-CAR-RETURNS-IN', { t: timer(Date.parse(run!.finishAt) - now) })
              : <b class="pos">{tr('UI-HOME-CAR-BACK')}</b>}
          </div>
          {!t ? <Skeleton h={96} n={3} /> : t.available.map((r) => {
            const info = ROUTES[r.tripDefId];
            return (
              <button key={r.tripDefId} class="list-row" data-testid={`route-${r.tripDefId}`} style={{ minHeight: 96, opacity: r.available ? 1 : 0.75 }}
                onClick={() => {
                  if (r.available && st === 'home') app.nav.openSheet('MOD-TRIP-START', { tripDefId: r.tripDefId });
                  else if (!r.available && (r.lockedReason === 'node_condition' || r.lockedReason === 'node_level')) app.nav.openSheet('MOD-CAR-NODE', { nodeId: Object.keys(r.needs)[0] ?? 'engine' });
                }}>
                <span class="col" style={{ gap: 4, flex: 1 }}>
                  <b>{info?.name ?? r.tripDefId}</b>
                  {r.available
                    ? <span class="small muted">{tr('CL-CAR-ROUTE-LINE', { d: duration(r.durationS * 1000), wear: r.wearPct, risk: r.riskPct })}</span>
                    : <span class="small neg"><I n="lock" size={12} /> {ROUTE_LOCK[r.lockedReason ?? 'disabled'] ?? r.lockedReason}</span>}
                  <span class="small">{Object.entries(r.reward.currencies ?? {}).map(([k, v]) => amount(k, v)).join(' · ')}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

/** MOD-TRIP-START (ux/06 §4): время возвращения, износ, риск; энергию не тратит. */
export function TripStartSheet({ entry }: SheetProps) {
  const app = useApp();
  const id = String(entry.params['tripDefId']);
  const r = trips.value?.available.find((x) => x.tripDefId === id);
  const info = ROUTES[id];
  const key = useRef(app.idem());
  if (!r) return null;
  const send = async () => {
    try {
      const res = await app.call('startTrip', { body: { tripDefId: id }, idempotencyKey: key.current });
      trips.value = { ...trips.value!, running: res.trip };
      app.haptic('medium');
      app.nav.popSheet();
      void refresh(app).catch(() => {});
    } catch (e) {
      if (e instanceof ApiCallError && e.code === 'CAR_NOT_READY') { showToast(tr('CL-CAR-PATCH-FIRST'), 'error'); app.nav.openSheet('MOD-CAR-NODE', { nodeId: String(e.details?.['node'] ?? 'engine') }, { replace: true }); return; }
      app.handleError(e, { resource: tr('CL-CAR-WHAT') });
    }
  };
  return (
    <Sheet entry={entry} title={info?.name ?? id} actions={[
      { text: tr('CL-CAR-CANCEL'), tone: 'outline', onClick: () => app.nav.popSheet() },
      { text: tr('T-RIDE-GO'), testId: 'trip-send', disabled: !online.value, onClick: send },
    ]}>
      <div class="col" data-testid="MOD-TRIP-START-body">
        <span class="muted">{info?.lead}</span>
        <span>{tr('CL-CAR-RETURNS-AT', { time: new Date(Date.now() + r.durationS * 1000).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) })}</span>
        <span>{tr('CL-CAR-WEAR', { p: r.wearPct })}</span>
        <span>{tr('T-RIDE-RISK', { pct: r.riskPct })}</span>
        <span class="small muted">{tr('CL-CAR-RISK-EXPLAIN')}</span>
        <span class="small">{tr('CL-CAR-NO-ENERGY')}</span>
      </div>
    </Sheet>
  );
}

/** MOD-TRIP-RESULT: «Забрать добычу» — POST claim; награда из ответа сервера. */
export function TripResultSheet({ entry }: SheetProps) {
  const app = useApp();
  const tripId = String(entry.params['tripId']);
  const run = trips.value?.running;
  const [res, setRes] = useState<ResponseOf<Api['claimTrip']> | null>(null);
  const info = run ? ROUTES[run.tripDefId] : undefined;
  useEffect(() => {
    app.call('claimTrip', { params: { tripId }, body: {} }).then((r) => {
      setRes(r);
      setWallet(r.wallet);
      app.haptic('success');
      void refresh(app).catch(() => {});
    }).catch((e: unknown) => {
      if (e instanceof ApiCallError && e.code === 'ALREADY_CLAIMED') { void refresh(app).catch(() => {}); app.nav.popSheet(); return; }
      app.handleError(e, { resource: tr('CL-CAR-WHAT') });
      app.nav.popSheet();
    });
  }, []);
  return (
    <Sheet entry={entry} title={info?.name} actions={[{ text: tr('UI-BTN-COLLECT'), testId: 'trip-result-ok', disabled: !res, onClick: () => app.nav.popSheet() }]}>
      <div class="col" data-testid="MOD-TRIP-RESULT-body">
        {!res ? <Skeleton h={60} /> : (
          <>
            <span>{res.broken ? info?.broken : info?.back}</span>
            <div class="row" style={{ flexWrap: 'wrap', gap: 8 }}>
              {Object.entries(res.rewards.currencies ?? {}).map(([k, v]) => <span key={k} class="badge soft">+{amount(k, v)}</span>)}
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}
